import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect } from 'react';
import { SidePanel } from '@alfa-bank/newclick-composite-components/side-panel';
import { useApiMethod } from '@alfa-bank/newclick-core/hooks';
import cn from 'classnames';
import { Feature } from '../../constants/feature';
import { loginStates, voipScreens } from '../../constants/types';
import { callerPhoneNumber, callerPhoneNumberAnonymous } from '../../constants/voip-settings';
import { useVoipContext } from '../../hooks/use-voip-context';
import { useWidgetContext } from '../../hooks/use-widget-context';
import { FeatureToggle } from '../feature-toggle';
import { CancelledCall } from './without-active-call/cancelled-call';
import { FailedCall } from './without-active-call/failed-call';
import { ErrorMicroNotAccess } from './error-micro-not-access';
import { Skeleton } from './skeleton';
import { VoipWithActiveCall } from './with-active-call';
import { VoipWithoutActiveCall } from './without-active-call';
import styles from './index.module.css';
export const Voip = ({ toggleBottomSheet }) => {
    const { hasFeature, getPhoneInformationApiMethod, setBackground, trackInAnalytics, needImmediatelyStartCall, setImmediatelyStartCall, isWebview, isPreAuth, } = useWidgetContext();
    const callSupportAvailable = hasFeature(Feature.CallSupport);
    const { startCall, accessMicroStatus, haveDevices, loginStatus, voipScreen, setLoginStatus, activeCall, } = useVoipContext();
    const { getPhoneInformationData, getPhoneInformationFetch } = useApiMethod(getPhoneInformationApiMethod, {
        onSuccess: () => {
            const number = getPhoneInformationData
                ? getPhoneInformationData.mainPhone
                : callerPhoneNumber;
            startCall(number);
        },
        onError: () => {
            startCall(callerPhoneNumber);
        },
        disableCache: true,
    });
    const handleStartCall = useCallback(() => {
        if (activeCall || !callSupportAvailable)
            return;
        trackInAnalytics('VOIP_START_CALL');
        setLoginStatus(loginStates.PRELOAD);
        if (isPreAuth) {
            startCall(callerPhoneNumberAnonymous);
            return;
        }
        getPhoneInformationFetch({});
    }, [
        isPreAuth,
        activeCall,
        callSupportAvailable,
        getPhoneInformationFetch,
        startCall,
        setLoginStatus,
        trackInAnalytics,
    ]);
    useEffect(() => {
        if (needImmediatelyStartCall) {
            handleStartCall();
            setImmediatelyStartCall(false);
        }
    }, [handleStartCall, needImmediatelyStartCall, setImmediatelyStartCall]);
    useEffect(() => {
        const color = loginStatus === loginStates.PRELOAD || voipScreen === voipScreens.WITHOUT_CALL
            ? 'default'
            : 'gradient';
        setBackground(color);
    }, [loginStatus, setBackground, voipScreen]);
    const getTextMicroStatus = () => {
        if (haveDevices === 'NOT_MICRO') {
            return {
                title: 'Микрофон не найден',
                text: 'Проверьте наличие микрофона на вашем устройстве',
            };
        }
        return null;
    };
    if (accessMicroStatus === 'DENIED' || haveDevices === 'NOT_MICRO') {
        return (_jsx(ErrorMicroNotAccess, { ...getTextMicroStatus(), dataTestId: 'error-micro-not-access' }));
    }
    if (!callSupportAvailable) {
        return _jsx(FeatureToggle, {});
    }
    if (loginStatus === loginStates.PRELOAD) {
        return (_jsx(SidePanel.Body, { className: cn({ [styles.sidePanelBodyWebview]: isWebview }), children: _jsx(Skeleton, {}) }));
    }
    switch (voipScreen) {
        case voipScreens.WITHOUT_CALL:
            return (_jsx(VoipWithoutActiveCall, { handleStartCall: handleStartCall, toggleBottomSheet: toggleBottomSheet }));
        case voipScreens.WITH_CALL:
            return _jsx(VoipWithActiveCall, {});
        case voipScreens.FAILED_CALL:
            return _jsx(FailedCall, { startCall: handleStartCall, toggleBottomSheet: toggleBottomSheet });
        case voipScreens.TERMINATED_CALL:
            return _jsx(CancelledCall, {});
        case voipScreens.CANCELLED_CALL:
            return _jsx(CancelledCall, {});
        default:
            return null;
    }
};
